import React from 'react'
import { AITripPreferenceStep } from './GenerateTripPage'

export interface LocationFact {
  text: string
  detail?: string
}

type LocationFactsMap = {
  [key: string]: Array<LocationFact>
}

export const locationFacts: LocationFactsMap = {
  Sydney: [
    {
      text: 'The Sydney Opera House uses 1,056,006 roof tiles',
      detail: 'Those iconic white tiles are made from a Swedish porcelain — and they actually clean themselves in the rain!',
    },
    {
      text: 'Sydney Harbour is the world\'s largest natural harbour',
      detail: 'It stretches over 240 km of shoreline and holds more water than the world\'s next four largest natural harbours combined.',
    },
    {
      text: 'Enough steel went into the Sydney Harbour Bridge to build eight Eiffel Towers',
      detail: 'Its arch spans 503 m and the top is 134 m above sea level at mid-span.',
    },
    {
      text: 'The world\'s first surf lifesaving club was founded at Bondi Beach in 1907',
      detail: 'Those red-and-yellow flags you see started right here.',
    },
    {
      text: 'Sydney\'s Royal Botanic Garden is Australia\'s oldest scientific institution',
      detail: 'It\'s been cultivating exotic and native plants since 1816.',
    },
    {
      text: 'There\'s a secret beach at Milk Beach in Vaucluse',
      detail: 'Tucked beneath the cliffs of Watsons Bay, it\'s reached via a short but steep walking path.',
    },
    {
      text: 'Humpback whales cruise past Cape Solander during their annual migration',
      detail: 'From May to November, thousands of humpbacks hug the coast — volunteers log more than 3,000 sightings each season.',
    },
    {
      text: 'The Rocks district is Sydney\'s oldest neighbourhood',
      detail: 'Walk its cobbled laneways to feel like you\'ve stepped back into the 1800s—complete with hidden pubs.',
    },
  ],
  Melbourne: [
    {
      text: 'Melbourne has the largest tram network in the world',
      detail: 'More than 250 km of double track spider across the suburbs, linking 1,700 stops. The oldest section opened in 1884 and some heritage W-class trams still rumble along city routes.',
    },
    {
      text: 'Melbourne’s Royal Exhibition Building is UNESCO-listed',
      detail: 'Completed in 1880 for the International Exhibition, it hosted Australia’s first Federal Parliament in 1901. Its soaring dome was inspired by Florence’s Duomo.',
    },
    {
      text: 'The city’s coffee culture rivals any in the world',
      detail: 'Melburnians consume more espresso per capita than most global cities. Annual competitions like the World Barista Championship often feature Aussie finalists.',
    },
    {
      text: 'Melbourne hosts the Australian Open tennis tournament',
      detail: 'As the first Grand Slam of the year, it attracts hundreds of thousands of fans each January. Matches continue late into balmy summer nights under retractable-roof arenas.',
    },
    {
      text: 'The Yarra River once flowed “upside-down”',
      detail: 'Early European settlers nicknamed it that because heavy clay silt made the water look brown and opaque. Extensive river-clean-up projects have since improved clarity.',
    },
    {
      text: 'Hosier Lane is a living graffiti gallery',
      detail: 'Street artists repaint its bluestone walls almost daily, so the artwork is never exactly the same. Official city policy protects the laneway as a legal street-art zone.',
    },
    {
      text: 'Locals joke about “four seasons in one day”',
      detail: 'Melbourne’s position between hot inland plains and the cool Southern Ocean can swing temperatures by 10 °C within hours. Always pack both sunscreen and a jacket.',
    },
    {
      text: 'Melbourne Zoo opened in 1862',
      detail: 'It is the oldest zoo in Australia and now cares for over 300 species. Visitors can stay overnight during special “Roar & Snore” camps.',
    },
  ],
  Perth: [
    {
      text: 'Perth is the sunniest capital city in Australia',
      detail: 'Meteorologists record an average of 8 hours of sunshine per day across the year. Clear skies mean spectacular Indian Ocean sunsets almost nightly.',
    },
    {
      text: 'It is closer to Jakarta than Sydney',
      detail: 'Indonesia’s capital lies about 3,000 km away, while Sydney is nearly 3,300 km distant. This west-coast isolation shapes Perth’s laid-back vibe.',
    },
    {
      text: 'Kings Park is one of the world’s largest inner-city parks',
      detail: 'Covering 400 ha, it outsizes New York’s Central Park. Hundreds of native wildflowers bloom there each spring.',
    },
    {
      text: 'Perth’s beaches stretch for 19 km',
      detail: 'White-sand favourites like Cottesloe and Scarborough offer easy bus access from the CBD. Afternoon sea-breezes, nicknamed “the Fremantle Doctor”, cool the shoreline.',
    },
    {
      text: 'The Swan River is named for its native black swans',
      detail: 'These graceful birds feature on Perth’s city flag and state emblem. Kayakers share the river with dolphins that hunt bream near the Narrows Bridge.',
    },
    {
      text: 'Rottnest Island is home to the quokka',
      detail: 'These smiling marsupials have made the island an Instagram sensation. Selfies are fine, but feeding wildlife is strictly prohibited.',
    },
    {
      text: 'Perth has a thriving craft-beer scene',
      detail: 'More than 60 micro-breweries operate across the metropolitan area. Many pour experimental ales flavoured with native bush ingredients.',
    },
    {
      text: 'The skyline glows under more than 300 sunny days a year',
      detail: 'Long daylight hours power Perth’s growing solar-panel uptake, which is the highest of any Australian capital.',
    },
  ],
  Brisbane: [
    {
      text: 'The Brisbane River snakes through the city for 76 km',
      detail: "Locals affectionately call it the 'Brown Snake' because of its winding shape and muddy colour. River-cat ferries and paddle-steamers ply its bends daily.",
    },
    {
      text: 'The Story Bridge can be climbed',
      detail: 'Adventure seekers scale its 80-m steel summit for panoramic skyline views. Night climbs offer a glittering perspective of the CBD lights.',
    },
    {
      text: 'Brisbane hosts the world’s largest koala sanctuary',
      detail: 'Lone Pine Sanctuary opened in 1927 and now cares for over 130 koalas. Visitors can also meet kangaroos, wombats and platypuses.',
    },
    {
      text: 'The city enjoys 283 sunny days a year',
      detail: 'A humid-subtropical climate encourages an outdoor lifestyle of riverside cycling and rooftop bars. Short, mild winters rarely drop below 10 °C.',
    },
    {
      text: 'South Bank Parklands were once an industrial wharf',
      detail: 'Redeveloped for Expo 88, the precinct now houses galleries, a beach lagoon and weekly artisan markets. It is Brisbane’s cultural heart.',
    },
    {
      text: 'Brisbane is Australia’s third-largest city',
      detail: 'Despite rapid growth, locals insist it keeps a relaxed, small-town friendliness. Many commuters still greet bus drivers by name.',
    },
    {
      text: 'The Gabba stadium dates back to 1895',
      detail: 'It hosts cricket Tests and AFL matches for 42,000 fans. A major rebuild is planned for the 2032 Olympic Games.',
    },
    {
      text: 'Jacaranda season turns the city purple each October',
      detail: 'University students jokingly call the bloom a reminder to start studying for exams. Fallen petals form carpets along suburban streets.',
    },
  ],
  Adelaide: [
    {
      text: 'Adelaide is nicknamed the “City of Churches”',
      detail: 'Nineteenth-century settlers granted freedom of worship, leading to a skyline of spires. Today festivals and food often overshadow its religious heritage.',
    },
    {
      text: 'It was Australia’s first planned city',
      detail: 'Colonel William Light designed the 1837 grid with wide boulevards and surrounding parklands. His plan still shapes traffic flow and leafy vistas.',
    },
    {
      text: 'Adelaide hosts one of the world’s biggest arts festivals',
      detail: 'The Adelaide Fringe welcomes over 7,000 artists each February–March. Pop-up venues transform laneways into buzzing performance spaces.',
    },
    {
      text: 'More than 900 ha of green space encircle the CBD',
      detail: 'The Park Lands are heritage-listed and remain public commons for sport, picnics and native-plant conservation.',
    },
    {
      text: 'Three major wine regions sit within an hour’s drive',
      detail: 'The Barossa, McLaren Vale and Adelaide Hills lure day-trippers with cellar doors and shiraz tastings. BYO designated driver is highly advised.',
    },
    {
      text: 'The South Australian Museum holds the world’s largest display of Aboriginal artefacts',
      detail: 'Over 30,000 cultural items showcase First Nations history from across the continent. Many pieces are displayed in consultation with traditional owners.',
    },
    {
      text: 'Adelaide Oval has hosted cricket since 1871',
      detail: 'Its grassy northern hill and heritage scoreboard are beloved quirks amid modern grandstands. The footbridge links fans directly to the city centre.',
    },
    {
      text: 'Haigh’s Chocolates was founded here in 1915',
      detail: 'Australia’s oldest family-owned chocolate maker still roasts its own cocoa beans on site. Free factory tours hand out generous samples.',
    },
  ],
  Darwin: [
    {
      text: 'Darwin is closer to Bali than Sydney',
      detail: 'The Indonesian island lies just over 2½ hours away by plane, while Sydney is a four-hour flight. Its tropical proximity shapes cuisine rich in Asian flavours.',
    },
    {
      text: 'Cyclone Tracy nearly destroyed the city in 1974',
      detail: 'Winds stripped 70 % of buildings on Christmas Day, prompting Australia’s largest peacetime evacuation. Much of modern Darwin was rebuilt to new cyclone-proof standards.',
    },
    {
      text: 'The city has two distinct seasons',
      detail: 'The Dry runs from May to October with blue skies and 30 °C days, while the Wet brings dramatic monsoon storms. Locals celebrate the first rains with open-air showers.',
    },
    {
      text: 'Mindil Beach Sunset Market is a Top-End icon',
      detail: 'Every dry-season evening, food stalls, fire twirlers and buskers line the sand dunes. Satay skewers and mango smoothies are perennial favourites.',
    },
    {
      text: 'Saltwater crocodiles rule the waterways',
      detail: 'Tours on the Adelaide River showcase 5-m giants leaping for buffalo meat. The nearby “Cage of Death” lets brave visitors float beside a croc behind acrylic walls.',
    },
    {
      text: 'Darwin played a key role in WWII',
      detail: 'It endured 64 Japanese air raids in 1942—more bombs than fell on Pearl Harbor. Museum exhibits include a salvaged Spitfire and wharf wreckage.',
    },
    {
      text: 'The Museum and Art Gallery houses “Sweetheart”',
      detail: 'This 5.1-m crocodile was notorious for attacking outboard motors in the 1970s. It was accidentally drowned after becoming entangled in a trap.',
    },
    {
      text: 'Darwin Harbour is five times bigger than Sydney Harbour',
      detail: 'Mangroves fringe its vast, high-tidal estuary that supports dugongs, turtles and dolphins. Sunset cruises depart daily from the Waterfront precinct.',
    },
  ],
  'Gold Coast': [
    {
      text: 'The Gold Coast has over 70 km of coastline',
      detail: 'It’s home to icons like Surfers Paradise, Burleigh Heads and Snapper Rocks. World-class surf breaks here host annual Quiksilver Pro contests.',
    },
    {
      text: 'Surfers Paradise was originally called Elston',
      detail: 'The 1933 name-change was a publicity master-stroke that lured holidaymakers south from Brisbane. High-rise hotels quickly followed, defining the skyline we know today.',
    },
    {
      text: 'The Gold Coast has more canals than Venice',
      detail: 'About 400 km of man-made waterways weave past homes with private pontoons. Water taxis and recreational cruisers link restaurants straight to the back door.',
    },
    {
      text: 'The Q1 building is one of the tallest residential towers in the world',
      detail: 'Rising 322 m, it pierces the subtropical skyline like a silver needle. An express lift whooshes to the SkyPoint deck in just 42 seconds.',
    },
    {
      text: 'The Gold Coast hosts over 12 million visitors annually',
      detail: 'Schoolies Week alone draws tens of thousands of graduating students each November. Tourism joins film production and education as pillars of the local economy.',
    },
    {
      text: 'Burleigh Head National Park offers rainforest trails just minutes from the beach',
      detail: 'Wild turkeys and water dragons scuttle under pandanus as the track climbs to ocean lookouts. Humpback whales are often spotted passing the headland from May to October.',
    },
    {
      text: 'The area has a booming arts and culture scene',
      detail: 'HOTA (Home of the Arts) includes galleries, a lakeside amphitheatre and Australia’s tallest public art tower. Outdoor concerts there seat up to 5,000 stargazers.',
    },
    {
      text: 'The Gold Coast was a key filming location for major Hollywood movies',
      detail: 'Village Roadshow Studios’ eight sound stages hosted blockbusters like *Aquaman* and *Thor: Ragnarok*. Local beaches often double for far-flung tropical worlds.',
    },
  ],
  Katoomba: [
    {
      text: "Katoomba is home to the world's steepest passenger railway",
      detail: 'The Scenic Railway plunges into the Jamison Valley at a 52° pitch. Originally built for coal hauling, it now thrills tourists with glass-roof carriages.',
    },
    {
      text: "The name 'Katoomba' means 'shining falling water'",
      detail: "Derived from the Gundungurra word 'Kedumba', it honours the nearby cascades of Katoomba Falls. Indigenous rock art and lore permeate the surrounding cliffs.",
    },
    {
      text: 'Echo Point welcomes over two million visitors a year',
      detail: 'They come to photograph the Three Sisters rock pillars and sweeping eucalyptus haze. Sunrise turns the sandstone a vivid orange.',
    },
    {
      text: 'Katoomba’s crisp “champagne air” lured 1900s health retreats',
      detail: 'Grand guest-houses promised a tonic escape from Sydney smog. Many heritage-listed buildings still line the main street.',
    },
    {
      text: 'The Scenic Skyway glides 270 m above rainforest',
      detail: 'Its electro-chromic floor clouds over, then clears to reveal dizzying views straight down. On clear days you can spot Mount Solitary far across the valley.',
    },
    {
      text: 'The Winter Magic Festival paints the town rainbow every June',
      detail: 'Locals don costumes ranging from steampunk to fairies for a solstice street parade. Performers fill cafes with folk music and poetry slams.',
    },
    {
      text: 'Katoomba Street sits about 1,017 m above sea level',
      detail: 'That height brings cool summers and occasional snowfall in July. It is one of the highest main streets in Australia.',
    },
    {
      text: 'Coal once powered Katoomba’s tourism boom',
      detail: 'A 19th-century incline railway hauled shale to the valley floor. The repurposed tracks now whisk hikers back up the escarpment.',
    },
  ],
  'Hayman Island': [
    {
      text: 'Hayman is the northernmost of the Whitsunday Islands',
      detail: 'It lies just 33 km off Queensland’s coast on the Great Barrier Reef. Warm currents keep the surrounding coral gardens vibrant year-round.',
    },
    {
      text: 'The entire island operates as a single luxury resort',
      detail: 'Guests have exclusive run of 400 ha of rainforest, reef and beaches. Environmental officers oversee strict turtle-nesting and reef-conservation programs.',
    },
    {
      text: 'Hayman’s hexagonal pool holds seven Olympic pools’ worth of water',
      detail: 'Covering 7,300 m², it is one of the largest resort lagoons in the Southern Hemisphere. A sand-fringed inlet creates the illusion of a private beach inside the pool.',
    },
    {
      text: 'Sir Reginald Ansett opened the first hotel here in 1950',
      detail: 'The aviation pioneer used his seaplanes to ferry wealthy guests direct to paradise. Subsequent owners have continually re-imagined Hayman in six-star style.',
    },
    {
      text: 'Green sea turtles nest on Hayman’s beaches',
      detail: 'Hatchlings scramble to sea between November and March under ranger protection. Guests can join nightly walks to monitor emerging clutches.',
    },
    {
      text: 'Arrivals are by helicopter, seaplane or luxury launch',
      detail: 'The 15-minute hop from Hamilton Island offers aerial views of Heart Reef. Those prone to seasickness can even book a private charter jet.',
    },
    {
      text: 'Hayman sits within a UNESCO World Heritage area',
      detail: 'More than 1,500 fish species and 600 coral types thrive in its surrounding marine park. Divers regularly spot manta rays cruising the bommies.',
    },
    {
      text: 'The Beatles holidayed here in 1964',
      detail: 'They arrived incognito by boat for a rare mid-tour break. Memorabilia from the visit still hangs in the resort’s bar.',
    },
  ],
  Bali: [
    {
      text: "Bali is nicknamed the 'Island of the Gods'",
      detail: 'More than 20,000 Hindu temples dot its volcanic landscape, each linked to local guardian spirits. Offerings of flowers and incense line streets, hotels and even motorbikes every single day.',
    },
    {
      text: 'It’s Indonesia’s only Hindu-majority province',
      detail: 'Over 80 % of Balinese practise a unique blend of Hinduism and ancestral animism. Rituals follow both the Sanskrit calendar and older animist cycles.',
    },
    {
      text: 'Nyepi Day plunges the island into 24 hours of silence',
      detail: 'Airports close, lights dim and even beaches fall quiet for the Saka New Year. Locals use the day to meditate, while tourists enjoy star-filled night skies free of light pollution.',
    },
    {
      text: 'Mount Agung dominates at 3,031 m',
      detail: 'Balinese believe this active volcano is the earthly home of the gods. Pilgrims trek pre-dawn to Pura Besakih on its slopes for full-moon ceremonies.',
    },
    {
      text: 'Bali’s Subak rice terraces form a 1,000-year-old UNESCO irrigation system',
      detail: 'The co-operative water-sharing network reflects the Tri Hita Karana philosophy of harmony among people, nature and spirit. Farmers still meet at temples to set planting dates together.',
    },
    {
      text: 'Some eastern beaches shimmer black',
      detail: 'Volcanic minerals from Mount Agung tint the sand a dramatic charcoal hue. Photographers flock here at sunrise for mirrored reflections of the fiery skyline.',
    },
    {
      text: 'Kopi luwak—the world’s priciest coffee—hails from Bali',
      detail: 'Wild civets digest ripe arabica cherries, fermenting the beans before roasting. Ethical farms now focus on free-roaming animals rather than caged production.',
    },
    {
      text: 'A Balinese calendar cycle lasts just 210 days',
      detail: 'Known as the Pawukon system, it overlays ten concurrent week-lengths that guide festivals and temple anniversaries. Many families celebrate two birthdays each “western” year as a result.',
    },
  ],
  'Nusa Dua': [
    {
      text: "'Nusa Dua' means 'two islands'",
      detail: 'It refers to the twin islets just off the Bukit Peninsula that shelter the main lagoon. At low tide you can walk across to one of them for sunrise photos.',
    },
    {
      text: 'Nusa Dua was built from scratch in the 1970s',
      detail: 'The Bali Tourism Development Corporation reclaimed land to create Indonesia’s first master-planned resort enclave. Utilities run underground to keep palm-lined avenues uncluttered.',
    },
    {
      text: "Nusa Dua hosts one of Indonesia's largest convention centres",
      detail: 'The Bali International Convention Centre can seat more than 6,000 delegates. World leaders from APEC to the G20 have gathered under its soaring Balinese-roofed halls.',
    },
    {
      text: "The Pasifika Museum holds the world's largest collection of Pacific and Asia-Pacific art",
      detail: 'Over 2,500 works range from NZ Māori carvings to Gauguin’s Tahiti paintings. Air-conditioned galleries provide a cool cultural break from the beach.',
    },
    {
      text: "The 'Water Blow' at Tanjung Benoa sends waves 10 m into the air",
      detail: 'A narrow sea cave funnels surf into an explosive geyser—spectacular at high tide. Viewing decks keep spectators safe from the spray.',
    },
    {
      text: 'The Bali Collection is Nusa Dua’s open-air shopping and dining complex',
      detail: 'More than 30,000 m² house boutiques, art galleries and nightly live music. A free shuttle loops the resort zone every 15 minutes.',
    },
    {
      text: 'Turtle Island (Pulau Serangan) is just 5 km away',
      detail: 'The conservation centre rescues injured green and hawksbill turtles before releasing them. Visitors can help weigh hatchlings for health records.',
    },
    {
      text: 'Geger Beach is home to an ancient Balinese temple',
      detail: 'Pura Geger crowns a headland overlooking calm turquoise waters ideal for SUP boarding. Local seaweed farmers still harvest edible algae from the shallows at dawn.',
    },
  ],

  Seminyak: [
    {
      text: "Seminyak's beach is famous for its colourful beanbags and umbrellas",
      detail: 'Laid-back bars set them out each afternoon, turning the shoreline into a rainbow lounge. Sunset DJs spin chilled house beats while vendors wander with grilled corn.',
    },
    {
      text: "'Seminyak' comes from the Balinese words for 'reef' and 'harbour'",
      detail: 'What was once a quiet fishing stretch morphed into a luxury enclave in the 2000s. Many old jukung boats now serve as quirky beach-bar decor.',
    },
    {
      text: 'Seminyak hosts over 500 private villas—one of the highest luxury-villa densities in Bali',
      detail: 'Most come with plunge pools, open-air bathrooms and butler service. Digital nomads often rent these long-term for a taste of Balinese indoor-outdoor living.',
    },
    {
      text: 'Petitenget Temple dates back to the 6th century',
      detail: 'Locals believe it guards the coast against mischievous sea spirits. Colourful odalan festivals fill its courtyard with dance, gamelan and incense every 210 days.',
    },
    {
      text: 'Potato Head’s façade is built from 6,000 reclaimed wooden shutters',
      detail: 'Collected from all over Indonesia, the multicoloured panels wrap an amphitheatre-shaped beach club. Sustainability workshops inside teach guests about up-cycling.',
    },
    {
      text: 'Jalan Kayu Aya is home to the Seminyak Flea Market',
      detail: 'Over 60 stalls sell everything from rattan handbags to palm-leaf art. Haggling is expected, so start at half the first price and meet in the middle.',
    },
    {
      text: 'There’s a “secret” rooftop garden at the Seminyak Village mall',
      detail: 'Few shoppers realise an open-air terrace sits above the boutiques, offering rare ocean views. Yoga classes and craft workshops pop up there on weekends.',
    },
    {
      text: 'Double Six Beach was once a refuelling point for Japanese seaplanes in WWII',
      detail: 'Concrete moorings still lurk beneath the sand at low tide. Today the beach is a mellow dawn-patrol spot for long-boarders.',
    },
  ],
  Canggu: [
    {
      text: "Echo Beach's sand is black",
      detail: 'Volcanic minerals from Mount Agung lend it a dramatic, glittering hue. Sunset bonfire gatherings give the shoreline an otherworldly glow.',
    },
    {
      text: 'Tanah Lot Temple sits on a rocky islet just 15 km from Canggu',
      detail: "It's one of Bali's most photographed sunset viewpoints. At high tide waves isolate the shrine, enhancing its mystical aura.",
    },
    {
      text: 'Berawa Beach offers waves up to 3 m high',
      detail: "That makes it an intermediate-to-advanced surfer's playground. Beach breaks here are gentler than Uluwatu but punchier than Kuta.",
    },
    {
      text: 'Canggu was a sleepy rice-farming village until the late 2000s',
      detail: 'Now Instagram cafés, yoga shalas and co-working lofts dot the paddies. Many rice terraces remain in production beside designer villas.',
    },
    {
      text: 'The Love Anchor Market rotates local artisans every weekend',
      detail: 'Hand-made jewellery, home décor and gluten-free doughnuts share bamboo stalls. Live acoustic sets keep the Sunday vibe mellow.',
    },
    {
      text: 'Canggu Circle Mall features Bali’s first curved digital-media façade',
      detail: 'At night LEDs pulse with moving art and event promos. The architecture won a regional lighting-design award in 2023.',
    },
    {
      text: 'Deus Ex Machina began here as a motorcycle workshop',
      detail: 'It evolved into a cult lifestyle brand fusing surf, art and specialty coffee. Their monthly Sunday Sessions showcase indie bands in the back yard.',
    },
    {
      text: 'Pererenan Beach’s boardwalk is known as Bali’s best “Sunset Point”',
      detail: 'The timber path lets you stroll above crashing waves without sand in your shoes. Photographers gather early for silhouetted horse-ride shots.',
    },
  ],
  Ubud: [
    {
      text: "'Ubud' comes from the Balinese word for medicine",
      detail: "The area was once famed for its healing herbs and traditional 'balian' doctors. Wellness retreats and herbal apothecaries still trade on that centuries-old reputation today.",
    },
    {
      text: 'The Sacred Monkey Forest Sanctuary is home to around 700 long-tailed macaques',
      detail: 'Three 14th-century temples hide beneath giant banyan trees inside the reserve. Visitors are advised to stash sunglasses—these cheeky residents are notorious souvenir thieves.',
    },
    {
      text: 'Puri Saren Royal Palace was built in the early 1800s',
      detail: 'Members of the Ubud royal family still reside in part of the compound. Nightly legong dance performances light the courtyard with flickering oil lamps.',
    },
    {
      text: 'Taman Saraswati (the Water Palace) features a floating lotus-pond stage',
      detail: 'Completed in 1951, it honours Saraswati, the Hindu goddess of learning. Evenings see kecak and gamelan shows echo across thousands of blooming pink lotuses.',
    },
    {
      text: "The terraced rice fields around Ubud are part of Bali's UNESCO-listed Subak irrigation system",
      detail: 'Carved by hand over a thousand years, the terraces illustrate communal water sharing rooted in the Tri Hita Karana philosophy. Dawn treks reveal farmers chanting mantras as they set bamboo sluices.',
    },
    {
      text: 'Goa Gajah (“Elephant Cave”) is an archaeological site from the 11th century',
      detail: 'A menacing demon mouth marks the entrance to meditation chambers carved into rock. Dutch archaeologists only rediscovered the bathing pools in 1954 under a layer of jungle.',
    },
    {
      text: 'The Campuhan Ridge Walk was originally a royal horse trail',
      detail: 'This free, 2-km hilltop path offers panoramic valley views and cool breezes at sunrise. Art cafés at the village end serve coconut pancakes to reward early hikers.',
    },
    {
      text: 'The Bali Spirit Festival blends yoga, dance and live music',
      detail: 'Held each spring, it attracts thousands of practitioners from more than 50 countries. Proceeds support local environmental and educational charities around Ubud.',
    },
  ],
  Kuta: [
    {
      text: 'Kuta was originally a quiet fishing village',
      detail: "It didn't transform into a global backpacker hotspot until the surfer boom of the 1970s. Traces of its maritime roots linger in early-morning fish auctions near the south end of the beach.",
    },
    {
      text: 'Kuta Beach stretches about 5 km from Tuban up to Legian',
      detail: "It's one of Bali's longest continuous shorelines, perfect for sunrise jogs or sunset soccer. Gentle, rolling waves make it a beginner-friendly surf break.",
    },
    {
      text: "Waterbom Bali in Kuta has been voted Asia's #1 waterpark multiple times",
      detail: 'Spanning 3.8 ha, it packs in more than 17 adrenaline-pumping slides. Half of the park is landscaped with native plants to offset its carbon footprint.',
    },
    {
      text: "'Kuta' comes from the old Balinese word kutha, meaning fortress",
      detail: 'Historians believe a small stronghold once guarded this stretch of coast against pirates. Today only place-names like Banjar Kedonganan hint at the fortified past.',
    },
    {
      text: 'Every August Kuta hosts the Kuta Karnival',
      detail: 'Sand-sculpting contests, beach soccer tournaments and live music fill the foreshore for a week. Hotels collaborate on mass beach clean-ups before and after the party.',
    },
    {
      text: 'Kuta Art Market lights up nightly with 100+ stalls',
      detail: 'Vendors haggle over hand-woven ikats, carved masks and silver trinkets until late. It’s a lively spot to practise your Bahasa bargaining phrases.',
    },
    {
      text: 'The area still celebrates full-moon ceremonies at local temples',
      detail: 'Expect gamelan orchestras, fragrant offerings and dancers in gold-threaded sarongs. Respectful visitors are welcome with a sash and modest dress.',
    },
    {
      text: 'Ngurah Rai International Airport is just minutes from Kuta',
      detail: 'Its runways lie less than 3 km from the main strip, making Kuta one of Bali’s most convenient beach bases. Plane-spotters love photographing low-flying jets over the sand.',
    },
  ],
  Legian: [
    {
      text: 'Legian sits just north of Kuta yet feels a notch calmer',
      detail: 'It shares those legendary Bali sunsets—just with smaller crowds and wider beach space. Surf schools dot the shoreline for first-timers.',
    },
    {
      text: "'Legian' in old Balinese literally means 'good sand'",
      detail: 'The shoreline stretches more than 2 km and is cleaned at dawn by swarms of community volunteers. Its fine grains are ideal for barefoot jogs.',
    },
    {
      text: 'La Plancha pioneered Bali’s beachfront lounge scene in 2006',
      detail: 'Its rainbow umbrellas and beanbags quickly became a social-media icon. Dozens of copy-cat bars now echo its colour-pop style along the coast.',
    },
    {
      text: 'Jalan Padma Utara is lined with boutiques selling local silversmith craft',
      detail: 'Balinese filigree earrings and chunky cuffs sparkle in window-lit displays. Artisans often work at benches in the back, happy to explain techniques.',
    },
    {
      text: 'Legian celebrates purification ceremonies at Pura Dalem',
      detail: 'On full-moon nights, locals carry barong lion masks and bamboo gamelan down the street. Visitors are welcome if shoulders and knees are covered.',
    },
    {
      text: 'Padma Beach in Legian is a popular beginner surf spot',
      detail: 'Gentle, rolling waves and soft sand bottom minimise wipe-out bruises. Board rentals and lessons cluster around the lifeguard tower.',
    },
    {
      text: 'Legian rooftops offer panoramic views of South Bali',
      detail: 'Hotel sky-bars fill up at golden hour for cocktails above the palm skyline. Fireworks from nearby Kuta are visible on New Year’s Eve.',
    },
    {
      text: 'The Double Six Club helped launch Bali’s nightlife scene',
      detail: 'Although closed, its beachfront legacy lives on in modern venues like Jenja and Cocoon. Older expats still reminisce about its bungee jump over the dance floor.',
    },
  ],
  Jimbaran: [
    {
      text: 'Jimbaran Bay was once a humble fishing village famous for dawn fish markets',
      detail: 'The Kedonganan Market still bustles at 5 a.m. as fishermen unload their catch. Chefs from five-star resorts barter alongside locals.',
    },
    {
      text: 'Jimbaran Beach stretches for about 4 km of golden sand',
      detail: 'Its sheltered waters stay glassy even when the Bukit cliffs whip up surf elsewhere. Families favour the gentle shoreline for paddle-boarding.',
    },
    {
      text: "Jimbaran's beachfront seafood cafés let you pick your own catch",
      detail: 'Choose fish, prawns or lobster by weight, then relax as chefs grill it in smoky coconut husks. Dining barefoot under tiki torches is a Bali rite of passage.',
    },
    {
      text: "Jimbaran's famous sunset dinners under flame torches began in the 1970s",
      detail: 'What started as fishermen hosting beach barbecues grew into a nightly 300-table phenomenon. Reservations are still scribbled on banana leaves.',
    },
    {
      text: 'Uluwatu Temple sits on a cliff just west of Jimbaran Bay',
      detail: 'Its Kecak fire dance lights up the horizon after sunset. Many visitors stop in Jimbaran for seafood before the performance.',
    },
    {
      text: 'Traditional jukung boats still float offshore',
      detail: 'Their bright batik colours help owners spot them among hundreds anchored at low tide. Photographers love the patterned reflections at sunrise.',
    },
    {
      text: 'Jimbaran is popular for plane-spotting',
      detail: 'Jets skim low over the water on final approach to Ngurah Rai Airport. Beach cafés even list “arrival schedules” on chalkboards for aviation buffs.',
    },
    {
      text: 'Limestone bluffs frame the bay like a natural amphitheatre',
      detail: 'They glow peach-pink during the famed Jimbaran sunset. Local kids cliff-jump from hidden ledges at high tide.',
    },
  ],
  Uluwatu: [
    {
      text: "'Pura Luhur Uluwatu' perches atop a 70 m limestone cliff",
      detail: "Its name means 'Divine Temple of the Land's End', one of Bali's six key sea temples. Mischievous monkeys at the gate watch for unattended sunglasses.",
    },
    {
      text: 'Uluwatu Temple is guarded by a troupe of macaques',
      detail: 'They famously “trade” stolen items for fruit offered by local guides. Hold onto hats and phones while you admire the view.',
    },
    {
      text: 'You can catch the famous Kecak Fire Dance here at sunset',
      detail: 'Over 100 chanting men circle a blazing coconut-husk fire, re-enacting the Ramayana. The ocean horizon turns gold behind the open-air stage.',
    },
    {
      text: 'Uluwatu is a world-class surf mecca, with waves up to 6 m high',
      detail: "The feared 'Uluwatu Right' break has drawn pros since the 1970s. Access is via a cave stairwell that opens straight into pounding sets.",
    },
    {
      text: "There's a secret cave beneath the cliff",
      detail: 'Stone steps descend to a sheltered grotto once used for silent meditation. At low tide it reveals sea-carved archways perfect for photos.',
    },
    {
      text: 'Cliffside shrines line the Uluwatu walking path',
      detail: 'Pilgrims leave marigold offerings and incense at each little pavilion. Sunrise walkers often spot sea eagles gliding on updrafts.',
    },
    {
      text: 'Padang Padang Beach lies just below the temple',
      detail: 'A narrow rock tunnel leads to powder-white sand and turquoise water. It hosted the 2010 Rip Curl Cup and Julia Roberts in “Eat Pray Love”.',
    },
    {
      text: 'Blowholes beneath the cliffs send water jets skyward',
      detail: 'The spectacle peaks at high tide when swells slam cavern mouths below. Keep a safe distance—rogue spray can soak cameras from 50 m away.',
    },
  ],
  Singapore: [
    {
      text: 'Singapore is made up of 63 islands',
      detail: 'Beyond the main island, satellite isles like Sentosa, Pulau Ubin and St John’s each offer their own attractions. Several can be reached by cable-car, bumboat or even bicycle ferry.',
    },
    {
      text: 'The world’s tallest indoor waterfall lives at Changi Airport',
      detail: 'Jewel’s Rain Vortex plunges 40 m through a terraced rainforest atrium, sending mist across five storeys of shops. Nightly light-and-sound shows turn the cascade into a technicolour vortex.',
    },
    {
      text: 'Almost half the city is green space',
      detail: 'Tree-lined streets, pocket parks and rooftop gardens earn Singapore the nickname “City in a Garden.” Flagship projects like Gardens by the Bay showcase futuristic “Supertrees” that harvest solar power.',
    },
    {
      text: 'Chewing gum is famously restricted',
      detail: 'Only therapeutic gum is legal, keeping pavements spotless and trains litter-free. Fines for illegal import or spitting can reach S$2,000.',
    },
    {
      text: 'The MRT is fully driverless',
      detail: 'Automated trains on the Red and Green lines whisk about three million passengers around daily. Platform screen doors and A/C stations make commutes comfortable in tropical heat.',
    },
    {
      text: 'F1’s first night race roars through Marina Bay',
      detail: 'Since 2008, the Singapore Grand Prix has lit up a 23-corner street circuit with 1,600 floodlights. Drivers call the humid, stop-start course one of the toughest on the calendar.',
    },
    {
      text: 'The Merlion statue sprays 2,400 L of water a minute',
      detail: 'Half-lion, half-fish, the 8.6-m mascot symbolises Singapore’s maritime trading roots. Tourists queue day and night for the classic skyline selfie beside its fountain.',
    },
    {
      text: 'Hawker culture is UNESCO-listed',
      detail: 'Open-air food centres preserve dishes like chicken rice, laksa and satay at affordable prices. Tables are shared “chope-style” with tissue packets, fostering a uniquely communal dining scene.',
    },
  ],
  London: [
    {
      text: 'London’s buses weren’t always red',
      detail: 'In the 1800s each private operator painted its double-deckers a different colour so passengers could spot their route at a glance. The network was unified under the iconic red livery in 1907, creating a moving city-wide brand.',
    },
    {
      text: 'The London Underground is the world’s oldest metro system',
      detail: 'It opened in 1863 with steam trains puffing through brick-lined “cut-and-cover” tunnels. Today its 11 lines carry about five million passenger journeys on busy weekdays.',
    },
    {
      text: 'Big Ben doesn’t refer to the tower',
      detail: 'It is the nickname of the Great Bell that chimes inside the clock at the north end of the Palace of Westminster. The tower itself was renamed Elizabeth Tower in 2012 for Queen Elizabeth II’s Diamond Jubilee.',
    },
    {
      text: 'There are over 170 museums in London',
      detail: 'Flagships like the British Museum, Natural History Museum and Tate Modern all offer free entry thanks to public funding. Together they welcome more than 65 million visits a year, making culture remarkably accessible.',
    },
    {
      text: 'The River Thames has over 30 bridges in Greater London',
      detail: 'The oldest is London Bridge, though the current structure dates from 1973 with granite from its Victorian predecessor. Free pedestrian crossings make riverside walks one of the easiest ways to sight-see.',
    },
    {
      text: 'Tower Bridge once raised its bascules over 6,000 times a year',
      detail: 'Today it lifts only around 800 times annually—still an impressive feat of Victorian engineering. The hydraulics are original in design but now run on oil and electricity instead of steam.',
    },
    {
      text: 'London has more than 8 million trees',
      detail: 'That is roughly one tree per resident and provides 21 % canopy cover across the capital. In 2019 the city declared itself a “National Park City” to celebrate its unexpectedly green landscape.',
    },
    {
      text: 'The Shard is the tallest building in the UK',
      detail: 'Soaring 310 m, its glass “shards” were modelled on church spires painted by Canaletto. The open-air Skydeck on level 72 offers 360-degree views stretching up to 65 km on a clear day.',
    },
  ],
  Dubai: [
    {
      text: 'Dubai is home to the world’s tallest building',
      detail: 'The Burj Khalifa pierces the sky at 828 m and 163 floors, dwarfing surrounding skyscrapers. Visitors ride the world’s fastest double-deck elevators to an open-air deck on level 148.',
    },
    {
      text: 'DXB is the world’s busiest airport for international travellers',
      detail: 'More than 86 million passengers passed through in 2024, fuelled by Emirates’ globe-spanning hub-and-spoke network. Three terminals process up to 100 aircraft movements an hour.',
    },
    {
      text: 'The Dubai Metro is completely driverless',
      detail: 'Red and Green lines span 89 km of elevated and underground track, all controlled by a central computer. Every station is air-conditioned, and gold-class carriages offer front-window views.',
    },
    {
      text: 'Ski Dubai brings snow to the desert',
      detail: 'The 22,500 m² indoor slope maintains –4 °C year-round, with five runs and a chairlift beneath the Mall of the Emirates. Real king penguins waddle out for meet-and-greets several times a day.',
    },
    {
      text: 'Palm Jumeirah added 560 ha of land to the coastline',
      detail: 'The palm-shaped archipelago required 94 million m³ of sand dredged from the Gulf. A monorail now links luxury villas and the Atlantis resort to the mainland.',
    },
    {
      text: 'Gold vending machines exist here',
      detail: 'ATMs branded “Gold to Go” dispense everything from 1-g wafers to 10-g bars, updated to real-time bullion prices. Customers can even choose engraved designs as souvenirs.',
    },
    {
      text: 'Dubai Police patrol in supercars',
      detail: 'Their fleet includes a Bugatti Veyron, Lamborghini Aventador and Aston Martin One-77, all painted green-and-white. The eye-catching vehicles double as tourism ambassadors at public events.',
    },
    {
      text: 'Over 85 % of residents are expatriates',
      detail: 'More than 200 nationalities live side-by-side, creating a culinary scene that ranges from Afghan kebabs to Zimbabwean sadza. English functions as a common lingua franca alongside Arabic.',
    },
  ],
  'New York': [
    {
      text: 'Over 800 languages are spoken in New York City',
      detail: 'Queens alone is considered the most linguistically diverse urban area on Earth. Street signs in neighbourhoods like Jackson Heights read in Spanish, Bengali and Tibetan side-by-side.',
    },
    {
      text: 'Central Park is bigger than Monaco',
      detail: 'The 3.41-km² greensward welcomes roughly 42 million visitors each year. Horse-drawn carriages, paddle-boats and winter ice-skating change with the seasons.',
    },
    {
      text: 'The Empire State Building was built in just 410 days',
      detail: 'At peak, construction leapt four and a half stories per week—an unrivalled pace in 1931. The mooring mast on top was originally intended for dirigibles.',
    },
    {
      text: 'NYC’s subway spans 472 stations',
      detail: 'If its 392 track-km were laid end-to-end they would stretch well past Chicago. Express tracks let trains overtake locals in the same tunnel.',
    },
    {
      text: 'Times Square was named after a newspaper',
      detail: 'The *New York Times* moved its HQ there in 1904, though it stayed only nine years. The name and neon glow endured long after the presses moved on.',
    },
    {
      text: "The 'pizza-slice index' tracks subway fares",
      detail: 'Since the 1960s the price of a cheese slice has hovered close to the cost of a single ride. Economists still quote the ratio as a tongue-in-cheek inflation gauge.',
    },
    {
      text: 'The Statue of Liberty arrived in 350 pieces',
      detail: 'French workers packed the copper plates into 214 crates for the 1885 voyage. Reassembly on Bedloe’s Island took four months of riveting.',
    },
    {
      text: 'Grand Central has a secret Platform 61',
      detail: 'It once whisked President Franklin D. Roosevelt’s armour-plated limousine directly into the Waldorf Astoria garage. The track now lies dormant but intact beneath Park Avenue.',
    },
  ],

  Honolulu: [
    {
      text: 'ʻIolani Palace is America’s only royal residence',
      detail: 'The Hawaiian monarchs ruled from its gilded halls until the overthrow of 1893. Remarkably, the palace had electric lights four years before the White House.',
    },
    {
      text: 'Waikiki popularised modern surfing worldwide',
      detail: 'Olympic swimmer Duke Kahanamoku taught tourists to ride longboards here in the early 1900s. Today gentle rollers still make it the ultimate beginner break.',
    },
    {
      text: 'Diamond Head is an extinct volcanic crater',
      detail: 'A 99-step trail climbs to bunkers used as WWII lookout posts. The summit rewards hikers with panoramic views over Waikiki’s hotel strip.',
    },
    {
      text: 'The Honolulu Marathon is among the world’s largest',
      detail: 'Over 30,000 runners tackle the humid December course each year. There is no time limit, so thousands of first-timers walk the route to fireworks at the finish.',
    },
    {
      text: 'Hawaii consumes more Spam per capita than any U.S. state',
      detail: 'Locals slice the canned meat into sushi-style Spam musubi or fry it for breakfast. Even McDonald’s offers Spam and eggs on its island menus.',
    },
    {
      text: 'Ala Moana Center is the world’s biggest open-air mall',
      detail: 'More than 350 shops sprawl across four breezy levels linked by koi ponds and bridges. Each Christmas a 15-m tree lights up its central stage.',
    },
    {
      text: 'The USS Arizona Memorial floats above a sunken battleship',
      detail: 'Oil still beads to the surface—dubbed the “black tears of the Arizona.” The shrine honours 1,177 sailors lost in the 1941 Pearl Harbor attack.',
    },
    {
      text: 'Honolulu’s average temperature varies only 4 °C year-round',
      detail: 'Steady trade winds keep humidity in check and make the climate feel pleasantly mild. Locals joke they own more board shorts than jackets.',
    },
  ],
  Hawaii: [
    {
      text: 'Hawaii sits atop a volcanic hotspot',
      detail: 'Kīlauea in Hawaiʻi Volcanoes National Park has erupted almost continuously since 1983. Glow from its lava lake is often visible after dark from park lookouts.',
    },
    {
      text: 'It’s the only U.S. state with two official languages',
      detail: 'Hawaiian and English share equal legal status, while Pidgin creole is widely spoken in everyday conversation. Road signs increasingly carry bilingual names.',
    },
    {
      text: 'New land grows by about 42 acres a year',
      detail: 'Cooling lava flowing into the Pacific gradually extends the island’s shoreline. A 2018 eruption even created a new black-sand beach at Kapoho Bay.',
    },
    {
      text: 'Mauna Kea is the tallest mountain on Earth—if measured from base to peak',
      detail: 'From the ocean floor it towers around 10,210 m, beating Everest by nearly a kilometre. Its 4,207-m summit hosts world-class telescopes above 40 % of Earth’s atmosphere.',
    },
    {
      text: 'Hawaii has no daylight saving time',
      detail: 'Clocks stay the same all year, so sunset times drift only modestly with the seasons. Jet-lagged visitors appreciate the consistent early sunsets for stargazing.',
    },
    {
      text: 'The Hawaiian alphabet has just 12 letters',
      detail: 'Five vowels and seven consonants spell every word, plus the ʻokina glottal stop. This simplicity gives place names their rhythmic character—think **Haleakalā** and **Lāhainā**.',
    },
    {
      text: 'Humpback whales migrate here each winter',
      detail: 'Roughly 10,000 gather to breed between December and April, especially off Maui’s west coast. Breaching displays are visible even from beachfront cafés.',
    },
    {
      text: 'Rainbow licence plates honour Hawaii’s frequent arcs',
      detail: 'Moist trade winds and sharp mountain ridges create prime rainbow-making conditions. Meteorologists say the islands see more rainbows per day than anywhere else in the U.S.',
    },
  ],
  Nadi: [
    {
      text: 'Nadi International Airport handles 97 % of Fiji’s visitors',
      detail: 'Its WWII runways evolved into the nation’s main gateway and can now accommodate Airbus A380s. A modernisation in 2020 added open-air atriums filled with frangipani.',
    },
    {
      text: 'Sri Siva Subramaniya is the largest Hindu temple in the Southern Hemisphere',
      detail: 'Builders used traditional Dravidian stone-carving techniques over seven years. Intricate gopuram towers burst with rainbow colours against the tropical sky.',
    },
    {
      text: "'Nadi' means 'river' in Fijian",
      detail: 'The town grew on the fertile banks of the meandering Nadi River. Sugar-cane plantations still dominate the surrounding floodplain.',
    },
    {
      text: 'The Bula Festival has run since 1960',
      detail: 'Week-long parades, live music and a Lady Bula pageant raise funds for local charities. Street stalls sell kokoda (citrus-cured fish) and pineapple ice blocks.',
    },
    {
      text: 'Denarau Island was reclaimed from mangrove swamp',
      detail: 'A short causeway now links luxury resorts and an 18-hole championship golf course to the mainland. It’s the departure point for most Yasawa and Mamanuca cruises.',
    },
    {
      text: 'The Garden of the Sleeping Giant holds 2,000 orchid varieties',
      detail: 'Actor Raymond Burr started the collection in 1977, filling the foothills with dendrobiums and cattleyas. Raised boardwalks wind beneath giant Tahitian trees and lily ponds.',
    },
    {
      text: 'Nadi is launch-point for the Mamanuca Islands',
      detail: 'Over 20 postcard sand cays sit less than an hour away by fast catamaran. Day cruises offer snorkelling, coconut husking demos and kava ceremonies.',
    },
    {
      text: 'Traditional bure huts still dot nearby villages',
      detail: 'Palm-leaf walls and thatched roofs follow centuries-old construction methods. Roofs are replaced every five or six years during communal “somo” work parties.',
    },
  ],
  'Coral Coast': [
    {
      text: 'The Coral Coast stretches 50 km along Viti Levu’s south',
      detail: 'A fringing reef hugs the shoreline almost the entire way, creating calm lagoons. Snorkellers can spot clownfish just metres off the sand.',
    },
    {
      text: 'Sigatoka Sand Dunes were Fiji’s first national park',
      detail: 'Archaeologists uncovered 2,600-year-old Lapita pottery and ancient burial sites here. Interpretive walks reveal shifting dune ecosystems unlike anywhere else in Fiji.',
    },
    {
      text: 'Natadola Beach is often rated Fiji’s best',
      detail: 'Its deep lagoon allows safe swimming at any tide and horses trot the shoreline for sunset rides. The adjoining championship golf course overlooks turquoise reef.',
    },
    {
      text: 'The Coral Coast Railway runs sugar-cane trains turned tourist rides',
      detail: 'Open-air carriages chug from plantation fields to coastal villages, stopping for kava tastings. During harvest season, the same narrow-gauge line hauls cane to the Lautoka mill.',
    },
    {
      text: 'Kula Eco Park breeds endangered Fijian crested iguanas',
      detail: 'Visitors can hand-feed native parrots and hold juvenile boa constrictors. Profits fund mangrove replanting and school conservation workshops.',
    },
    {
      text: 'Tavuni Hill Fort was built by Tongan chiefs in the 1700s',
      detail: 'Clifftop earthworks defended against rival cannibal tribes. Interpretive signs retell grisly legends while offering panoramic river-valley views.',
    },
    {
      text: 'Beqa Lagoon nearby is famous for shark dives',
      detail: 'Bull, tiger and reef sharks gather for controlled hand-feeding at depths of 20 m. Divers wear chain-mail gloves while guides carry aluminium poles for safety.',
    },
    {
      text: 'Villagers still perform traditional fire-walking ceremonies',
      detail: 'According to legend the skill was a gift from the spirit god Tui Namoliwai. Participants stroll barefoot across 500 °C stones without burns.',
    },
  ],
  'Xuyen Moc': [
    {
      text: 'Xuyên Mộc boasts 32 km of pristine coastline',
      detail: 'Untouristed stretches like Hồ Cốc and Hồ Tràm curve along the East Sea under casuarina pines. Quiet weekdays make them perfect for solitary sunrise picnics.',
    },
    {
      text: 'Bình Châu–Phước Bửu Nature Reserve protects 11,300 ha of forest',
      detail: 'Wild elephants, macaques and sun bears roam its dipterocarp habitat. Birders come seeking the rare Siamese fireback pheasant on misty dawn trails.',
    },
    {
      text: 'Bình Châu Hot Springs bubble at up to 82 °C',
      detail: 'Locals boil eggs in wicker baskets for onsen-style snacks beside mineral pools. Resorts pipe the sulphur-rich water into cedar tubs for therapeutic soaks.',
    },
    {
      text: 'Pepper plantations blanket inland hills',
      detail: 'Tours walk you through vines where green berries sun-dry into fragrant black peppercorns. Visitors can blend their own spice mix to take home.',
    },
    {
      text: 'Sea turtles nest on secluded sands',
      detail: 'Community patrols relocate eggs to hatcheries, shielding them from monitor lizards. Hatchlings are released at sunset between April and October.',
    },
    {
      text: 'Dragon-fruit farms glow under night lights',
      detail: 'Illumination mimics moonlight, tricking cactus vines into off-season flowering and yielding two crops a year. The fields look like alien gardens from the highway.',
    },
    {
      text: 'Operation Frequent Wind evacuees departed this coast in 1975',
      detail: 'Thousands crammed aboard fishing junks to reach U.S. Navy ships offshore during the final days of the Vietnam War. Local elders still recount the hectic exodus.',
    },
    {
      text: 'Ca trù folk songs echo at Bình Châu temple festivals',
      detail: 'Female vocalists weave praise poetry over bamboo clappers and lute. UNESCO lists the genre as Intangible Cultural Heritage in need of safeguarding.',
    },
  ],
  'Ho Tram': [
    {
      text: 'The Grand Ho Tram was Vietnam’s first integrated casino resort',
      detail: 'Its twin towers now total over 1,100 rooms facing a 2.2 km private beach. Foreign passport holders can roll dice 24/7 on a Vegas-size gaming floor.',
    },
    {
      text: 'The Bluffs golf course ranks among Asia’s top five',
      detail: 'Greg Norman sculpted fairways through 50-m sand dunes with sweeping sea views. Wind shifts can change club choice by three shots on a single hole.',
    },
    {
      text: 'Ho Tram served as a Viet Cong supply route',
      detail: 'Jungle paths linked coastal hamlets to the Hồ Chí Minh Trail. Remnants of hidden bunkers still dot casuarina groves behind the resort strip.',
    },
    {
      text: 'Local fishermen dry squid on bamboo racks daily',
      detail: 'Sun-bleached strips are grilled roadside as the ultimate beer snack. Morning markets auction the freshest catches before tourists wake.',
    },
    {
      text: 'Kitesurfers flock to steady onshore winds',
      detail: 'Prime conditions blow from November to March when waves average 1–2 m. The town now hosts Vietnam’s National Kiteboarding Championships each February.',
    },
    {
      text: 'Nearby Minh Đạm Mountain hides wartime tunnels',
      detail: 'A summit pagoda offers panoramic coastal views after short but steep stair climbs. A small museum displays photos and recovered weapons.',
    },
    {
      text: 'Ho Tram’s mangroves teem with mudskippers and fiddler crabs',
      detail: 'Kayak tours weave through emerald channels where kingfishers dive for shrimp. Researchers have recorded 74 bird species in the estuary.',
    },
    {
      text: 'A sea-turtle rehab centre opened in 2024',
      detail: 'Injured greens and hawksbills are nursed back to health before weekly beach releases. Visitors can sponsor satellite tags to track migration routes.',
    },
  ],
  'Ho Chi Minh City': [
    {
      text: 'Over 8.9 million motorbikes buzz through the city',
      detail: 'That’s nearly one bike for every resident, forming a river of headlights at rush hour. Locals navigate the flow using subtle honk languages and hand signals.',
    },
    {
      text: 'Saigon’s Central Post Office was designed by Eiffel’s firm',
      detail: 'Inside, wrought-iron vaults and marble floors evoke a 19th-century railway station. Antique phone booths still function along its polished walls.',
    },
    {
      text: 'Bến Thành Market has traded since 1870',
      detail: 'Its clock-tower facade is a beloved city emblem featuring on souvenir T-shirts. Vendors sell everything from lotus tea to knock-off sneakers under one steamy roof.',
    },
    {
      text: 'Tanks burst through Reunification Palace gates on 30 April 1975',
      detail: 'A replica star-emblazoned tank now sits on the lawn as a photogenic memorial. Inside, war-era map rooms and rotary phones remain frozen in time.',
    },
    {
      text: 'Cà phê sữa đá blends coffee with condensed milk',
      detail: 'Cafés drip robusta through metal phin filters directly onto ice for 15 minutes. Sidewalk stools fill from dawn to midnight with caffeine devotees.',
    },
    {
      text: 'Bitexco Tower’s helipad juts out like a lotus petal',
      detail: 'At 262 m it was Vietnam’s tallest building in 2010, now out-soared by Landmark 81. The 49th-floor Skydeck delivers 360-degree city panoramas.',
    },
    {
      text: 'The city changed its name from Saigon in 1976',
      detail: 'It honours revolutionary leader President Hồ Chí Minh, yet locals still casually call the downtown core “Saigon.” Street signs display both names interchangeably.',
    },
    {
      text: 'Cu Chi Tunnels stretch 120 km beneath nearby forests',
      detail: 'Guerrillas cooked, lived and fought in this subterranean maze during the war. Today widened sections let tourists crawl through and sample cassava rations.',
    },
  ],
  Phuket: [
    {
      text: 'Phuket is Thailand’s largest island',
      detail: 'Covering 543 km², it’s roughly the size of Singapore. The Sarasin and Thao Thep Krasattri bridges connect it to the mainland province of Phang Nga.',
    },
    {
      text: 'The Big Buddha statue stands 45 m tall',
      detail: 'Clad in 135 t of Burmese white marble, it gleams in sunrise light on Nakkerd Hill. A steep road rewards climbers with sweeping views of Chalong Bay.',
    },
    {
      text: 'Phuket Old Town flaunts sino-Portuguese mansions',
      detail: 'Tin-mining wealth funded these pastel shophouses in the 19th century. Many now host Peranakan museums, chic cafés and street-art murals.',
    },
    {
      text: 'The Vegetarian Festival features extreme body piercing',
      detail: 'Devotees walk on hot coals and skewer cheeks with swords to channel deities and banish evil. White-clad parades fill streets with firecrackers every October.',
    },
    {
      text: 'Similan Islands off Phuket rank among the world’s top dive sites',
      detail: 'Visibility can exceed 30 m in the Andaman Sea, revealing manta rays and whale sharks. Live-aboard boats run multi-day trips during November–May’s dry season.',
    },
    {
      text: 'Bangla Road pulses with neon nightlife',
      detail: 'The 400-m strip in Patong closes to traffic at sunset, exploding with go-go bars and LED street performers. Drink hawkers compete with EDM bass until dawn.',
    },
    {
      text: 'Phuket was a Maritime Silk Road trading hub',
      detail: 'Arab, Indian and Chinese merchants once anchored for tin and spices, shaping a fusion cuisine. The yearly Old-Town Festival celebrates this multicultural heritage.',
    },
    {
      text: "Locals almost renamed the island 'Thalang' in 2011",
      detail: 'The proposal aimed to revive Phuket’s pre-colonial name but stalled in parliament after tourism groups objected. “Phuket” therefore remains the global brand.',
    },
  ],
  Patong: [
    {
      text: 'Patong Beach curves for almost 3 km',
      detail: 'Lifeguards mark safe swimming zones with red-yellow flags and jet-ski lanes. Parasailers colour the skyline during calm afternoon seas.',
    },
    {
      text: 'The 2004 tsunami hit Patong hard',
      detail: 'Waves surged 6 m high, yet the town rebuilt within a year with new early-warning sirens. Evacuation routes and blue tsunami markers now line the streets.',
    },
    {
      text: 'Bangla Walking Street boasts over 200 bars',
      detail: 'LED signs, live bands and street magicians vie for attention amid thumping EDM. Patong’s nightlife runs until 4 a.m. on peak nights.',
    },
    {
      text: 'Freedom Beach hides just south of Patong',
      detail: 'Reachable only by long-tail boat or a jungle trail, its entry fee funds daily waste collection. Powder-white sand stays surprisingly crowd-free even in high season.',
    },
    {
      text: 'Jungceylon Mall is named after an old Western word for Phuket',
      detail: 'A basement aquarium tunnel lets shoppers walk beneath tropical fish while escaping monsoon showers. Nightly fountain shows choreograph water jets to Thai pop.',
    },
    {
      text: 'Patong Carnival kicks off high season every November',
      detail: 'Floats depict sea dragons and Thai myths while fireworks light the bay. Hotels offer street-food stalls so visitors can taste local dishes in one stroll.',
    },
    {
      text: "OTOP Market promotes 'One Tambon One Product'",
      detail: 'Stall fees funnel profits back to artisans’ home villages, supporting rural crafts. Handmade batik sarongs and coconut bowls are popular buys.',
    },
    {
      text: 'Tri Trang Beach shelters one of Patong’s last coral reefs',
      detail: 'Snorkellers find Nemo-like clownfish just metres offshore at low tide. Coral nurseries have been installed to revive damaged sections.',
    },
  ],
  'Koh Samui': [
    {
      text: 'Samui Airport has thatched-roof open-air terminals',
      detail: 'Privately owned by Bangkok Airways, it feels more like a resort than an airport. Passengers taxi past frangipani gardens instead of concrete jetways.',
    },
    {
      text: 'The island exports two million coconuts a month',
      detail: 'Trucks ferry them to Surat Thani for processing into oil, milk and sugar. Trained macaques once harvested many of the nuts, though mechanical pickers are now common.',
    },
    {
      text: 'Big Buddha (Wat Phra Yai) rises 12 m above the sea',
      detail: 'Built in 1972, the golden statue is linked to the mainland by a short causeway. Its steep staircase offers sweeping photo ops at sunrise.',
    },
    {
      text: 'Full-moon parties began on nearby Koh Phangan',
      detail: 'High-speed ferries shuttle revellers across the channel all night for beachfront EDM. Dawn boats return crowds clutching fluorescent paint and sand-filled shoes.',
    },
    {
      text: "The 'Buddha’s Magic Garden' hides at 500 m altitude",
      detail: 'Farmer-sculptor Nim Thongsuk spent 1976-87 carving hundreds of mossy statues in a jungle clearing. Trickling waterfalls and mist lend the site an otherworldly feel.',
    },
    {
      text: 'Samui Regatta attracts 500 sailors each May',
      detail: 'Races loop around neighbouring islands, boosting the Gulf’s yachting profile ahead of major Asian events. Spectators watch colourful spinnakers from Chaweng’s shore-side bars.',
    },
    {
      text: 'Hin Ta and Hin Yai rocks resemble male and female anatomy',
      detail: 'Legend says star-crossed lovers were turned to stone by sea spirits, explaining the suggestive shapes. Vendors nearby sell phallic coconut candies for a cheeky souvenir.',
    },
    {
      text: 'Samui banned single-use plastic bags in 2015',
      detail: 'Reusable bamboo straws and cloth totes are now standard in markets and cafés. The initiative inspired other Thai islands to adopt similar eco-measures.',
    },
  ],
  Generic: [
    {
      text: 'Airplane food tastes different on purpose',
      detail: 'Cabin air dulls taste buds and dries noses, so airlines often double the salt to compensate.',
    },
    {
      text: 'There are nearly 30 places named "London" around the world',
      detail: 'Beyond the UK capital, you’ll find Londons in Canada, Ohio, Kiribati and more.',
    },
    {
      text: 'The world’s most visited city isn’t New York or Paris',
      detail: 'Bangkok regularly tops global tourism charts, thanks to its food, nightlife and affordability.',
    },
    {
      text: 'The London Eye was never meant to be permanent',
      detail: 'Built for the millennial celebrations in 2000, the wheel proved so popular it stayed.',
    },
    {
      text: 'The world’s shortest scheduled flight takes under 60 seconds',
      detail: 'Loganair’s Westray–Papa Westray hop in Scotland spans just 2.7 km.',
    },
    {
      text: 'Travel supports more than one in ten jobs worldwide',
      detail: 'Pre-pandemic, tourism employed roughly 330 million people across the globe.',
    },
    {
      text: 'Rome hosts a museum devoted entirely to pasta',
      detail: 'The Museo della Pasta has 11 rooms exploring its history, science, shapes and art.',
    },
    {
      text: 'The Eiffel Tower was nearly torn down—until radio transmissions saved it',
      detail: 'Slated for removal in 1909, its new role as a strategic radio mast secured its future.',
    },
    {
      text: 'Some airports ignore daylight saving time',
      detail: 'Phoenix Sky Harbor, for instance, stays on Mountain Standard Time all year.',
    },
    {
      text: 'You can fly around the world faster going east',
      detail: 'Westerly jet streams give eastbound aircraft a helpful tail-wind that can slice hours off long-haul trips.',
    },
    {
      text: 'Finland has almost as many saunas as cars',
      detail: 'Around 3 million saunas serve 5.5 million Finns—close to the nation’s 3.7 million passenger vehicles.',
    },
    {
      text: 'Tromsø, Norway sees no sunrise for six weeks',
      detail: 'During polar night (late November to mid-January) the sun never rises above the horizon.',
    },
    {
      text: 'Switzerland technically has no capital city',
      detail: 'Bern hosts parliament, but the Swiss constitution never designates an official capital.',
    },
    {
      text: 'The Great Wall of China isn’t a single wall',
      detail: 'It’s a network of fortifications stretching about 21,000 km across northern China.',
    },
    {
      text: 'Malana, India, speaks a mysterious local dialect',
      detail: 'Kanashi is unique to the village; legends link it to Greek soldiers, but linguists trace Tibeto-Burman roots.',
    },
    {
      text: 'Monaco is smaller than New York’s Central Park',
      detail: 'The principality covers 2.02 km², while Central Park spans roughly 3.4 km².',
    },
    {
      text: 'You can ride a zipline from Spain to Portugal',
      detail: 'Limite Zero glides over the Guadiana River—the world’s only cross-border zipline.',
    },
    {
      text: 'Canada has more lakes than the rest of the world combined',
      detail: 'Roughly 60% of the planet’s lakes ≥ 0.1 km² lie within Canadian borders.',
    },
    {
      text: 'La Gomera in the Canaries has a whistled language',
      detail: 'Silbo Gomero lets residents converse across deep valleys using only whistles.',
    },
    {
      text: 'Bhutan measures progress in Gross National Happiness',
      detail: 'The index guides government decisions alongside traditional economic metrics.',
    },
    {
      text: 'London cabbies memorise around 25,000 streets',
      detail: 'Earning ‘The Knowledge’ licence can take years of scooter-based studying.',
    },
    {
      text: 'You can mail a coconut in Hawaii with no packaging',
      detail: 'Write an address, add stamps and the USPS will deliver the bare nut—no box required.',
    },
  ],
}

export const fallbackImageIds: Array<string> = [
  '5ozks72rwzry7f9k2m9n',
  'rpuuftaccpy35ymgxn86',
  'pm9wsigf28xbtjdrrnv',
  'khdm8sqv4llroip0gr3fa',
  'o67q4bymkgw9zeo7gzg',
]

export const stepImageIds: Record<AITripPreferenceStep, string | undefined> = {
  start: undefined,
  location: undefined,
  occupancy: 'mt1tdpsaa8j2mobp81z',
  datePicker: '7hifc74i83p7kds2fp',
  pace: '9w6i5jqu19hhyebmmzo',
  proximity: 'cnzq14sdc2j0afsjvvr',
  budget: 'kkflnjvd7sradtskysz',
  theme: 'a51onk9awf9id7rtcnmu',
  mustDo: '9ktjc7j8r7tqztli96v',
  foodStyle: 'nrhcjilarfw651sgkkmn',
  freeText: undefined,
}

export const animatedBackgroundIds = [
  'trip-fade-1',
  'trip-fade-2',
  'trip-fade-3',
]

export function AITripTitles(hotelCity: string) {
  return [
    <>We're shaping your <i>{hotelCity}</i> itinerary...</>,
    'Understanding your trip preferences and travel style...',
    'Reading reviews, blogs and local guides...',
    'Researching the best things to do...',
    'Finding restaurants and attractions near your hotel...',
    'Curating the right experiences for each day...',
    'Checking timings and transfers so your day flows smoothly...',
    'Finding hidden gems beyond the tourist spots...',
    'Personalising your trip to match your style...',
    'Making sure every moment counts...',
    'Scouring the internet for the best insider tips from locals...',
    'Double-checking details so nothing gets missed...',
    'Adding the final touches...',
  ]
}
